import {
  Box,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { LOCALE_CATALOGUE, localeMeta } from "@shared/i18n";
import { useTranslation } from "react-i18next";
import type { PremiseConfig } from "@shared/types/foyer";
import type { PremiseCommit } from "../context/PremiseWorkspace";

interface Props {
  premise: PremiseConfig;
  /** Persist one change now and toast (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}

/**
 * Chip-based picker for the premise's supported locales + default
 * locale. Every multilingual text input downstream reads from this
 * list, so adding/removing here propagates to the editors.
 */
export default function SupportedLocalesCard({ premise, onCommit }: Props) {
  const { t } = useTranslation(["templates", "common"]);
  const supported = premise.supportedLocales ?? [];
  const defaultLocale = premise.defaultLocale ?? supported[0] ?? "en";
  const name = premise.premiseName;
  const label = (code: string) => {
    const m = localeMeta(code);
    return `${m.native} (${m.code.toUpperCase()})`;
  };

  // Both edits save at once, and are applied to the SAVED languages rather
  // than to what this render saw, so two quick clicks cannot undo each other.
  function toggle(code: string) {
    const adding = !supported.includes(code);
    void onCommit(
      (c) => {
        const cur = c.supportedLocales ?? [];
        const curDefault = c.defaultLocale ?? cur[0] ?? "en";
        let next = cur.includes(code)
          ? cur.filter((x) => x !== code)
          : [...cur, code];
        // Always include the default locale - if you just removed it,
        // promote the next surviving locale (or fall back to "en").
        let nextDefault = curDefault;
        if (!next.includes(curDefault)) {
          nextDefault = next[0] ?? "en";
          if (!next.length) next = [nextDefault];
        }
        return { ...c, supportedLocales: next, defaultLocale: nextDefault };
      },
      {
        success: adding
          ? t("supportedLocalesCard.added", { language: label(code), name })
          : t("supportedLocalesCard.removed", { language: label(code), name }),
        failure: adding ? "addLanguageFor" : "removeLanguageFor",
        failureParams: { language: label(code), name },
      }
    );
  }

  function setDefault(code: string) {
    void onCommit(
      (c) => {
        const cur = c.supportedLocales ?? [];
        return {
          ...c,
          supportedLocales: cur.includes(code) ? cur : [...cur, code],
          defaultLocale: code,
        };
      },
      {
        success: t("supportedLocalesCard.defaultSet", {
          name,
          language: label(code),
        }),
        failure: "setDefaultLanguageFor",
        failureParams: { name },
      }
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">
            {t("supportedLocalesCard.title")}
          </Typography>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              {t("common:labels.enabled")}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {LOCALE_CATALOGUE.map((loc) => {
                const on = supported.includes(loc.code);
                return (
                  <Chip
                    key={loc.code}
                    label={`${loc.code.toUpperCase()} · ${loc.native}`}
                    onClick={() => toggle(loc.code)}
                    color={on ? "primary" : "default"}
                    variant={on ? "filled" : "outlined"}
                    size="small"
                    data-testid={`locale-chip-${loc.code}`}
                  />
                );
              })}
            </Stack>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              {t("supportedLocalesCard.defaultLanguage")}
            </Typography>
            <Select
              size="small"
              value={defaultLocale}
              onChange={(e) => setDefault(String(e.target.value))}
              sx={{ minWidth: 220 }}
              inputProps={{ "data-testid": "locale-default" }}
            >
              {(supported.length > 0 ? supported : [defaultLocale]).map(
                (code) => {
                  const m = localeMeta(code);
                  return (
                    <MenuItem key={code} value={code}>
                      {`${m.code.toUpperCase()} · ${m.native}`}
                    </MenuItem>
                  );
                }
              )}
            </Select>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
